import type { Node, Edge } from '@xyflow/react';

import { tokens } from '@design';
// ============================================================
// Types
// ============================================================

export interface TreeNode {
  id: number;
  name: string;
  name_ar: string;
  unit_type: 'department' | 'section' | 'subsidiary';
  parent: number | null;
  order: number;
  employees_count: number;
  strategic_goals?: { id: number; title: string }[];
  children: TreeNode[];
}

export interface TreeResponse {
  tree: TreeNode[];
  primary_color: string;
  secondary_color: string;
}

export interface OrgNodeData {
  treeNode: TreeNode;
  label: string;
  level: number;
  isExpanded: boolean;
  childrenCount: number;
  primaryColor: string;
  secondaryColor: string;
  onToggleExpand: (nodeId: number) => void;
  onNodeClick: (node: TreeNode) => void;
}

// ============================================================
// Circular Reference Check
// ============================================================

export function isAncestorOf(
  potentialAncestorId: string,
  nodeId: string,
  edges: Edge[],
): boolean {
  const parentMap = new Map<string, string>();
  for (const edge of edges) {
    parentMap.set(edge.target, edge.source);
  }
  let current: string | undefined = nodeId;
  const visited = new Set<string>();
  while (current !== undefined) {
    if (current === potentialAncestorId) return true;
    if (visited.has(current)) break;
    visited.add(current);
    current = parentMap.get(current);
  }
  return false;
}

// ============================================================
// Tree → React Flow Conversion
// ============================================================

export function treeToNodesEdges(
  tree: TreeNode[],
  options: {
    primaryColor: string;
    secondaryColor: string;
    expandedNodeIds: Set<number>;
    onToggleExpand: (nodeId: number) => void;
    onNodeClick: (node: TreeNode) => void;
  },
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  function walk(treeNode: TreeNode, level: number) {
    const nodeId = String(treeNode.id);
    const isExpanded = options.expandedNodeIds.has(treeNode.id);
    const childrenCount = treeNode.children?.length ?? 0;

    nodes.push({
      id: nodeId,
      type: 'orgUnit',
      position: { x: 0, y: 0 }, // dagre will set this
      data: {
        treeNode,
        label: treeNode.name_ar || treeNode.name,
        level,
        isExpanded,
        childrenCount,
        primaryColor: options.primaryColor,
        secondaryColor: options.secondaryColor,
        onToggleExpand: options.onToggleExpand,
        onNodeClick: options.onNodeClick,
      } satisfies OrgNodeData,
    });

    if (treeNode.parent !== null) {
      edges.push({
        id: `e-${treeNode.parent}-${treeNode.id}`,
        source: String(treeNode.parent),
        target: nodeId,
        type: 'smoothstep',
        style: { stroke: tokens.colors.raw.gray["400"], strokeWidth: 1.5 },
      });
    }

    if (isExpanded && treeNode.children) {
      for (const child of treeNode.children) {
        walk(child, level + 1);
      }
    }
  }

  for (const root of tree) {
    walk(root, 0);
  }

  return { nodes, edges };
}

// ============================================================
// Collect all node IDs from tree
// ============================================================

export function collectAllIds(nodes: TreeNode[]): number[] {
  const ids: number[] = [];
  function walk(node: TreeNode) {
    ids.push(node.id);
    node.children?.forEach(walk);
  }
  nodes.forEach(walk);
  return ids;
}
